import {useEffect, useMemo, useRef, useState} from 'react';

import {useVendors} from 'src/hooks/use-vendors';
import {getRulesByVendorId} from 'src/openapi/admin/apis/ApprovalRuleApi';
import {getAssignUsersCount} from 'src/openapi/admin/apis/VendorManagementApi';
import {useAppDispatch} from 'src/store';

import type {ApprovalRule} from 'src/openapi/admin/models/ApprovalRule';
import type {Vendor} from 'src/openapi/admin/models/Vendor';
import type {VendorUserCount} from 'src/openapi/admin/models/VendorUserCount';

export type VendorTableRow = Vendor & {
    assignedUsers?: number,
    rules?: ApprovalRule[],
    isUsersLoading?: boolean,
    isRulesLoading?: boolean,
};

type Return = {
    vendors: VendorTableRow[],
    loading: boolean,
    reload: () => Promise<void>,
    rawVendors: Vendor[],
};

export const useVendorTableData = (filterText: string): Return => {
    const dispatch = useAppDispatch();
    const {vendors: rawVendors, loading: baseLoading, reload} = useVendors();
    const [assignedUsersMap, setAssignedUsersMap] = useState<Record<string, number | undefined>>({});
    const [rulesMap, setRulesMap] = useState<Record<string, ApprovalRule[] | undefined>>({});
    const [usersLoadingMap, setUsersLoadingMap] = useState<Record<string, boolean>>({});
    const [rulesLoadingMap, setRulesLoadingMap] = useState<Record<string, boolean>>({});
    const usersFetched = useRef<Set<string>>(new Set());
    const rulesFetched = useRef<Set<string>>(new Set());

    const sortedVendors = useMemo(() => {
        return [...rawVendors].sort((a, b) => a.vendorName.localeCompare(b.vendorName));
    }, [rawVendors]);

    useEffect(() => {
        const controller = new AbortController();
        let isActive = true;

        sortedVendors.forEach(vendor => {
            if (!usersFetched.current.has(vendor.id)) {
                usersFetched.current.add(vendor.id);
                setUsersLoadingMap(prev => ({...prev, [vendor.id]: true}));
                dispatch(getAssignUsersCount({vendorId: vendor.id}, {signal: controller.signal}))
                    .then(response => {
                        if (!isActive) {
                            return;
                        }
                        const payload = response.payload;
                        const assignedUsers = payload?.success
                            ? ((payload.data as VendorUserCount)?.userCount ?? 0)
                            : 0;
                        setAssignedUsersMap(prev => ({...prev, [vendor.id]: assignedUsers}));
                    })
                    .catch(() => {
                        if (!isActive) {
                            return;
                        }
                        setAssignedUsersMap(prev => ({...prev, [vendor.id]: 0}));
                    })
                    .finally(() => {
                        if (isActive) {
                            setUsersLoadingMap(prev => ({...prev, [vendor.id]: false}));
                        }
                    });
            }

            if (!rulesFetched.current.has(vendor.id)) {
                rulesFetched.current.add(vendor.id);
                setRulesLoadingMap(prev => ({...prev, [vendor.id]: true}));
                dispatch(getRulesByVendorId({vendorId: vendor.id}, {signal: controller.signal}))
                    .then(response => {
                        if (!isActive) {
                            return;
                        }
                        const payload = response.payload;
                        const rules = payload?.success ? (payload.data as ApprovalRule[]) ?? [] : [];
                        setRulesMap(prev => ({...prev, [vendor.id]: rules}));
                    })
                    .catch(() => {
                        if (!isActive) {
                            return;
                        }
                        setRulesMap(prev => ({...prev, [vendor.id]: []}));
                    })
                    .finally(() => {
                        if (isActive) {
                            setRulesLoadingMap(prev => ({...prev, [vendor.id]: false}));
                        }
                    });
            }
        });

        return () => {
            isActive = false;
            controller.abort();
        };
    }, [dispatch, sortedVendors]);

    const normalizedFilter = filterText.trim().toLowerCase();

    const vendorsWithMeta = useMemo(() => sortedVendors.map(vendor => ({
        ...vendor,
        assignedUsers: assignedUsersMap[vendor.id],
        rules: rulesMap[vendor.id],
        isUsersLoading: usersLoadingMap[vendor.id],
        isRulesLoading: rulesLoadingMap[vendor.id],
    })), [assignedUsersMap, rulesLoadingMap, rulesMap, sortedVendors, usersLoadingMap]);

    const filteredVendors = useMemo(() => {
        if (!normalizedFilter) {
            return vendorsWithMeta;
        }
        return vendorsWithMeta.filter(vendor => {
            const matchesName = vendor.vendorName.toLowerCase().includes(normalizedFilter);
            const matchesDescription = vendor.description?.toLowerCase().includes(normalizedFilter) ?? false;
            return matchesName || matchesDescription;
        });
    }, [vendorsWithMeta, normalizedFilter]);

    return {
        vendors: filteredVendors,
        loading: baseLoading,
        reload,
        rawVendors: sortedVendors,
    };
};
